import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI, surveyAPI } from '../../utils/api';

const DashboardHome = ({ currentUser }) => {
  const [stats, setStats] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      if (currentUser?.role === 'admin') {
        const [surveysRes, statsRes] = await Promise.all([
          fetch('/api/surveys?page=1', { credentials: 'same-origin' }).then(r => r.json()),
          analyticsAPI.getDashboard().catch(() => null)
        ]);
        setSurveys(surveysRes.surveys || surveysRes.data || surveysRes);
        if (statsRes) setStats(statsRes.data);
      } else {
        const response = await surveyAPI.getMySurveys();
        setSurveys(response.data);
      }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'grid',
      gap: '2rem'
    },
    welcome: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '.5rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem'
    },
    statCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      textAlign: 'center',
      height: '100px',
    },
    statValue: {
      fontSize: '3rem',
      fontWeight: 'bold',
      color: '#016e8c',
      margin: '0.3rem'
    },
    statLabel: {
      color: '#6c757d',
      fontSize: '1rem'
    },
    surveysSection: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '2rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    surveyCard: {
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      marginBottom: '1rem',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      border: '1px solid #dee2e6'
    },
    badge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.875rem',
      fontWeight: 'bold',
      marginLeft: '0.5rem'
    },
    published: {
      backgroundColor: '#28a745',
      color: 'white'
    },
    draft: {
      backgroundColor: '#ffc107',
      color: '#000'
    },
    button: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#07638e',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-block',
      marginTop: '1rem'
    }
  };

  if (loading) return <div style={styles.welcome}>Loading...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.welcome}>
        <h1>Welcome to SurveyPilot!</h1>
        <p style={{fontSize: '1.1rem', color: '#6c757d', marginTop: '1rem'}}>
          {currentUser?.role === 'admin' 
            ? 'Manage surveys and view analytics to measure manager effectiveness.'
            : 'Provide anonymous feedback to help improve manager effectiveness.'}
        </p>
      </div>

      {currentUser?.role === 'admin' && stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.total_surveys || 0}</div>
            <div style={styles.statLabel}>Total Surveys</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.published_surveys || 0}</div>
            <div style={styles.statLabel}>Published Surveys</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.total_responses || 0}</div>
            <div style={styles.statLabel}>Total Responses</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{parseFloat(stats.average_rating || 0).toFixed(2)}</div>
            <div style={styles.statLabel}>Average Rating</div>
          </div>
        </div>
      )}

      <div style={styles.surveysSection}>
        <h2>{currentUser?.role === 'admin' ? 'Recent Surveys' : 'My Surveys'}</h2>
        
        {!surveys || surveys.length === 0 ? (
          <p style={{color: '#6c757d', textAlign: 'center', padding: '2rem'}}>
            No surveys available yet.
          </p>
        ) : (
          (Array.isArray(surveys) ? surveys : []).slice(0, 5).map(survey => {
            const basePath = currentUser?.role === 'admin' ? '/admin' : '/users';
            const statusColors = {
              completed: { bg: '#28a745', text: 'white' },
              in_progress: { bg: '#ffc107', text: '#000' },
              pending: { bg: '#6c757d', text: 'white' }
            };
            const statusColor = statusColors[survey.status] || statusColors.pending;
            
            return (
              <div
                key={survey.id}
                style={styles.surveyCard}
                onClick={() => navigate(`${basePath}/surveys/${survey.id}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <h3 style={{marginBottom: '0.5rem'}}>
                  {survey.title}
                  {currentUser?.role === 'admin' ? (
                    <span style={{...styles.badge, ...(survey.published ? styles.published : styles.draft)}}>
                      {survey.published ? 'Published' : 'Draft'}
                    </span>
                  ) : (
                    <span style={{...styles.badge, backgroundColor: statusColor.bg, color: statusColor.text}}>
                      {survey.status?.replace('_', ' ').toUpperCase()}
                    </span>
                  )}
                </h3>
                <p style={{color: '#6c757d', marginBottom: '0.5rem'}}>
                  {survey.description?.substring(0, 150)}...
                </p>
                <small style={{color: '#6c757d'}}>
                  {survey.questions_count || 0} questions
                  {currentUser?.role !== 'admin' && survey.progress !== undefined && ` • ${survey.progress}% complete`}
                </small>
              </div>
            );
          })
        )}

        {surveys && Array.isArray(surveys) && surveys.length > 5 && (
          <button onClick={() => navigate(`${currentUser?.role === 'admin' ? '/admin' : '/users'}/surveys`)} style={styles.button}>
            View All Surveys →
          </button>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;
